import React from "react";
import moment from "moment/moment";
import { Avatar, Tooltip } from "@fluentui/react-components";
import { Eye, Download } from "lucide-react";
import HoverCard from "../hoverCard/HoverCard";
import { utcToLocalDateTimeString } from "../../utils/systemSettings";
import styles from "./WsrReportsView.module.scss";

const formatPeriod = (period) => {
  if (!period) return "";
  const parsed = moment(period.toString(), "YYMMDD");
  if (!parsed.isValid()) return period;
  const start = parsed.clone().subtract(4, "days");
  return `${start.format("MMM D")} - ${parsed.format("MMM D, YYYY")} (${period})`;
};

const TableSkeleton = ({ rowCount = 6 }) => (
  <div className={styles.tableSkeleton}>
    <div className={styles.skeletonTableHeader}>
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className={`${styles.shimmer} ${styles.skeletonHeaderCell}`} />
      ))}
    </div>
    {Array.from({ length: rowCount }).map((_, i) => (
      <div key={i} className={styles.skeletonTableRow}>
        {Array.from({ length: 4 }).map((_, j) => (
          <div key={j} className={`${styles.shimmer} ${styles.skeletonCell}`} />
        ))}
      </div>
    ))}
  </div>
);

const ActionIcon = ({ tooltip, onClick, children }) => (
  <Tooltip content={tooltip} relationship="label">
    <button type="button" className={styles.cardActionIcon} onClick={onClick}>
      {children}
    </button>
  </Tooltip>
);

const WsrReportsTableView = ({ reports, loading, settings, onPreview, onDownload }) => {
  if (loading && (!reports || reports.length === 0)) {
    return <TableSkeleton />;
  }

  if (!reports || reports.length === 0) {
    return <div className={styles.emptyState}>No locked reports yet.</div>;
  }

  return (
    <div className="table-responsive">
      <table className="table table-hover align-middle">
        <thead>
          <tr>
            <th>Period</th>
            <th>Confirmed By</th>
            <th>Confirmed At</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {reports.map((report) => (
            <tr key={report.period}>
              <td>{formatPeriod(report.period)}</td>
              <td>
                {report.confirmedBy ? (
                  <HoverCard
                    CanSendMessage={false}
                    HoverCardTrigger={
                      <div className="d-flex align-items-center">
                        <Avatar
                          size={24}
                          color="colorful"
                          name={report.confirmedBy.name}
                          image={{ src: report.confirmedBy.image }}
                        />
                        <span className="ms-2">{report.confirmedBy.name}</span>
                      </div>
                    }
                    AvatarId={report.confirmedBy.id}
                    AvatarName={report.confirmedBy.name}
                    AvatarImage={report.confirmedBy.image}
                    AvatarDesignation={report.confirmedBy.title}
                    AvatarEmail={report.confirmedBy.email}
                    AvatarPhone={report.confirmedBy.phoneNumber}
                    AvatarIsExternal={report.confirmedBy.isExternal}
                  />
                ) : (
                  "-"
                )}
              </td>
              <td>{report.confirmedAt ? utcToLocalDateTimeString(settings, report.confirmedAt) : "-"}</td>
              <td className="text-end">
                <div className="d-flex align-items-center justify-content-end gap-1">
                  <ActionIcon tooltip="Preview" onClick={() => onPreview(report)}>
                    <Eye size={15} />
                  </ActionIcon>
                  <ActionIcon tooltip="Download" onClick={() => onDownload(report)}>
                    <Download size={15} />
                  </ActionIcon>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {loading && <div className={styles.loadMoreSpinner} />}
    </div>
  );
};

export default WsrReportsTableView;
